import type { CountyAdapter, ParsedDocument } from "../types";

// Lender / institutional grantee keywords (multi-word first).
const LENDER_KEYWORDS = [
  "HOUSING AND URBAN DEVELOPMENT",
  "HOUSING AND URBAN DEVEOPMENT",
  "HOUSING AND COMMUNITY AFFAIRS",
  "VETERANS AFFAIRS",
  "VETERAN AFFAIRS",
  "SECRETARY OF",
  "DEPARTMENT OF",
  "CITY OF",
  "COUNCIL OF",
  "CREDIT UNION",
  "FEDERAL SAVINGS",
  "NATIONAL BANK",
  "STATE BANK",
  "BANK OF",
  "BANK ",
  "MORTGAGE",
  "FUNDING",
  "LENDING",
  "FINANCIAL",
  "FINANCE OF",
  "FROST BANK",
  "PRIMELENDING",
  "ROCKET MORTGAGE",
  "UNITED WHOLESALE",
  "GUARANTEED RATE",
  "CARDINAL FINANCIAL",
  "NATIONS LENDING",
  "AMCAP",
  "DHI MORTGAGE",
  "THRIVE MORTGAGE",
  "SFMC LP",
  "ARVEST BANK",
  "PROSPERITY BANK",
  "SOUTHWEST BANK",
  "SOUTHWEST HERITAGE",
  "AMERICAN MOMENTUM",
  "FIRSTCAPITAL BANK",
  "FIRST CAPITAL BANK",
  "FIRST NATIONAL BANK",
  "LONE STAR STATE BANK",
  "COMMUNITY NATIONAL BANK",
  "COMMERCIAL NATIONAL BANK",
  "INTERNATIONAL BANK OF COMMERCE",
  "LUBBOCK NATIONAL BANK",
  "HIGHLANDS RISIDENTIAL",
  "HIGHLANDS RESIDENTIAL",
  "GATEWAY MORTGAGE",
  "PREMIA MORTGAGE",
  "ALL WESTERN MORTGAGE",
  "MORTGAGE SOLUTIONS",
  "KWIK MORTGAGE",
  "NEW AMERICAN FUNDING",
  "HUNTINGTON NATIONAL",
  "EVERETT FINANCIAL",
  "T2 FINANCIAL",
  "CITY LENDING",
  "CITY BANK",
  "DIRECTIONS EQUITY",
  "LRS FINANCIAL",
  "MIDLAND HABITAT",
  "MADDACARS",
  "FIRST COMMUNITY CREDIT",
  "BANK OF AMERICA",
  "BANK OF COMMERCE",
  "FIRST BANK AND TRUST",
];

const containsLenderKeyword = (s: string): boolean => {
  const upper = s.toUpperCase();
  return LENDER_KEYWORDS.some((kw) => upper.includes(kw));
};

const ENTITY_END_SUFFIXES = [
  "LLC", "INC", "LP", "LTD", "CORP", "CORPORATION", "L L C",
  "TRUSTEE", "TRUST", "II", "III", "IV", "JR", "SR", "NA", "N A",
];

const splitParties = (combined: string): [string, string | null] => {
  const trimmed = combined.trim();
  if (!trimmed) return [trimmed, null];

  const suffixRe = new RegExp(
    `\\b(${ENTITY_END_SUFFIXES.join("|")})(?=[A-Z])`,
    "g",
  );
  let m: RegExpExecArray | null;
  while ((m = suffixRe.exec(trimmed)) !== null) {
    const splitAt = m.index + m[1].length;
    const left = trimmed.slice(0, splitAt).trim();
    const right = trimmed.slice(splitAt).trim();
    if (right.length < 5) continue;
    if (containsLenderKeyword(right)) return [left, right];
  }

  const upper = trimmed.toUpperCase();
  let bestIdx = -1;
  for (const kw of LENDER_KEYWORDS) {
    const idx = upper.indexOf(kw);
    if (idx > 0 && (bestIdx === -1 || idx < bestIdx)) bestIdx = idx;
  }
  if (bestIdx > 0) {
    return [trimmed.slice(0, bestIdx).trim(), trimmed.slice(bestIdx).trim()];
  }

  return [trimmed, null];
};

const DOC_TYPES = [
  "CORRECTION DEED OF TRUST",
  "SPECIAL WARRANTY DEED",
  "DEED OF TRUST",
  "WARRANTY DEED",
  "DEED",
];

const ADDRESS_RE =
  /^\d{1,6}\s+[A-Z0-9 .'-]+\s+(?:RD|ST|AVE|DR|CT|LN|BLVD|HWY|PL|WAY|CIR|TRL|PKWY|TER|CR)\b/i;

const parse = (rawText: string): ParsedDocument[] => {
  const lines = rawText
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const out: ParsedDocument[] = [];
  const docTypeAlt = DOC_TYPES.join("|");
  const lineRe = new RegExp(
    `^(.+?)\\s*(${docTypeAlt})\\s*(\\d{1,2}\\/\\d{1,2}\\/\\d{4})\\s*(\\d{4}-\\d+)\\s*--\\/--\\/--\\s*(.*)$`,
  );

  for (const line of lines) {
    const m = line.match(lineRe);
    if (!m) continue;
    const [, partiesBlob, docType, rawDate, docNumber, legalRaw] = m;

    const [mm, dd, yyyy] = rawDate.split("/");
    const filingDate = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;

    // Prefer whitespace-gap split (tab or 2+ spaces) when source is tab/space
    // separated; fall back to glued-entity split for the no-whitespace format.
    let grantorStr: string;
    let granteeStr: string | null;
    const gapSplit = partiesBlob.split(/\t+|\s{2,}/).map((s) => s.trim()).filter(Boolean);
    if (gapSplit.length >= 2) {
      grantorStr = gapSplit[0];
      granteeStr = gapSplit.slice(1).join(" ");
    } else {
      [grantorStr, granteeStr] = splitParties(partiesBlob);
    }
    const grantors = grantorStr ? [grantorStr] : [];
    const finalGrantees = granteeStr ? [granteeStr] : [];

    const legal = legalRaw.trim();
    const propertyAddress = ADDRESS_RE.test(legal) ? legal : undefined;

    // TX DOT rule: the Grantee is the Lender/Beneficiary (our lead). If the
    // grantee is an institutional lender (bank, mortgage co, HUD, state housing
    // agency, etc.), reject the record — it is NOT owner finance.
    const granteeUpper = (granteeStr || "").toUpperCase();
    const isInstitutional =
      !!granteeStr &&
      (containsLenderKeyword(granteeStr) ||
        /\b(LLC|INC|CORP|CORPORATION|LP|LTD|COMPANY|DBA)\b/.test(granteeUpper) ||
        granteeUpper.includes("SECRETARY OF") ||
        granteeUpper.includes("DEPARTMENT OF") ||
        granteeUpper.includes("STATE OF") ||
        granteeUpper.includes("HOUSING AND"));

    out.push({
      documentNumber: docNumber,
      documentType: docType,
      filingDate,
      grantors,
      grantees: finalGrantees,
      legalDescription: legal || undefined,
      propertyAddress,
      skipReason: isInstitutional ? "institutional lender (grantee)" : undefined,
    });
  }

  return out;
};

export const midlandAdapter: CountyAdapter = {
  countyId: "midland",
  countyLabel: "Midland County, TX - Deed of Trust Parser",
  description:
    "Paste Midland County search results (one record per line). Grantor/Grantee are glued together — parser splits on entity suffix + lender keyword boundary. TX DOT priority: Lender (Grantee) is the lead.",
  placeholder: `Paste Midland County records here (one per line)...

Expected format per line:
{GRANTOR}{GRANTEE}DEED OF TRUST{M/D/YYYY}{YYYY-####}--/--/--{legal or address}

Example:
EVOL PROPERTIES LLCFINANCE OF AMERICA MORTGAGE LLCDEED OF TRUST1/5/20232023-335--/--/--Subdivision- Name: COLLEGE HGTS ADDN Lot: 1,2 Block: 14`,
  detectionReasons: ["Midland County TX", "Deed of Trust"],
  confidenceScore: 75,
  filterRules: [
    "One record per line — grantor/grantee glued, split on entity suffix + lender keyword",
    "Records with institutional grantee (BANK/MORTGAGE/HUD/VA/etc) filtered as non-owner-finance",
    "Records without a street address in the legal column → flagged address_needs_review",
    "Family/bank/business filters from shared pipeline apply",
    "TX rule: reject DOTs under $75,000 (applied when loan amount is extracted later)",
  ],
  parse,
};
